(() => {
  async function subscribeToDailyDeals({ email, endpoint, locale }) {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        locale,
        consentTextVersion: 'daily-deals-v1',
      }),
    });

    if (!response.ok) throw new Error('Daily Deals subscription request failed');

    const result = await response.json();
    if (!result.ok) throw new Error('Daily Deals subscription request failed');

    return result;
  }

  class DailyDealsSubscription extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.trigger = this.querySelector('.daily-deals-subscription__trigger, [data-daily-deals-trigger]');
      this.modal = this.querySelector('dialog');
      this.closeButtons = this.querySelectorAll('[data-close]');
      this.form = this.querySelector('form');
      this.formView = this.querySelector('[data-form-view]');
      this.successView = this.querySelector('[data-success-view]');
      this.successTitle = this.successView.querySelector('.daily-deals-subscription__success-title');
      this.input = this.querySelector('input[type="email"]');
      this.status = this.querySelector('.daily-deals-subscription__status');
      this.submitButton = this.querySelector('[type="submit"]');

      this.trigger?.addEventListener('click', () => this.open());
      this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close()));
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        const submitted = this.submit();
        // An AI agent submitting the form as a WebMCP tool gets the outcome as the tool result
        submitted.catch(() => {}); // failures are already shown in the form
        if (event.agentInvoked) event.respondWith(submitted);
      });
      this.modal.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.modal.addEventListener('click', (event) => {
        if (event.target === this.modal) this.close();
      });
      this.modal.addEventListener('keydown', (event) => this.trapFocus(event));
    }

    open() {
      this.modal.showModal();
      window.requestAnimationFrame(() => {
        const target = this.successView.hidden
          ? this.input
          : this.successView.querySelector('[data-close]');
        target?.focus();
      });
    }

    close() {
      if (!this.modal.open) return;

      this.modal.close();
      this.trigger?.focus();
    }

    trapFocus(event) {
      if (event.key !== 'Tab') return;

      const focusable = [...this.modal.querySelectorAll('button:not([disabled]):not([hidden]), input:not([disabled]):not([hidden])')]
        .filter((element) => !element.closest('[hidden]'));
      const first = focusable[0];
      const last = focusable.at(-1);

      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    // Resolves with a confirmation, or rejects with the error shown to the visitor
    async submit() {
      this.clearError();

      if (!this.input.checkValidity()) {
        this.showError();
        this.input.focus();
        throw new Error(this.status.textContent);
      }

      this.setLoading(true);
      try {
        await subscribeToDailyDeals({
          email: this.input.value.trim(),
          endpoint: this.dataset.subscriptionEndpoint,
          locale: this.dataset.subscriptionLocale || 'en',
        });

        this.formView.hidden = true;
        this.successView.hidden = false;
        this.modal.setAttribute('aria-labelledby', this.successTitle.id);
        this.successView.querySelector('[data-close]')?.focus();
        return 'Subscribed to MYMARA daily deals emails.';
      } catch {
        this.showError(this.status.dataset.requestErrorMessage);
        this.input.focus();
        throw new Error(this.status.textContent);
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(isLoading) {
      this.submitButton.disabled = isLoading;
      this.submitButton.textContent = isLoading
        ? this.submitButton.dataset.loadingLabel
        : this.submitButton.dataset.defaultLabel;
    }

    clearError() {
      this.status.hidden = true;
      this.status.textContent = '';
      this.input.removeAttribute('aria-invalid');
    }

    showError(message = this.status.dataset.invalidMessage) {
      this.status.textContent = message;
      this.status.hidden = false;
      this.input.setAttribute('aria-invalid', 'true');
    }
  }

  if (!customElements.get('daily-deals-subscription')) {
    customElements.define('daily-deals-subscription', DailyDealsSubscription);
  }
})();
